import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import styles from "./Home.module.css";
import {FilmDescription, Loader} from "../../components";
import FilmLists from "../../components/FilmLists/FilmLists";
import VideoPlayerPreview from "../../components/VideoPlayerPreview/VideoPlayerPreview";
import {PreviewCarouselSlideshow} from "../../components/PreviewCarousel/PreviewCarousel";
import {useGetPageResultsQuery, useLazyGetPageContentQuery} from "../../api/modules";
import {useUserRole} from "../../hooks/useUserRole";
import useResize from "../../hooks/useResize";
import helper from "../../constants/helper";
import {RootState} from "../../store";
import {TABLET_VERSION} from "../../constants";
import ReactPlayer from "react-player";

const PAGE_NAME = "HOME";
const PREVIEW_VIDEOS_COUNT = 5;

const Home = () => {
    const {t} = useTranslation("translations");
    const {innerWidth} = useResize();
    const role = useUserRole();

    const PROFILE_ID = useMemo(() => Number(localStorage.getItem(helper.EPIC_PROFILE_ID)), []);

    const paginationParams = useMemo(() => ({
        page_number: 1,
        rehydrated_pagination: true,
        profile_id: PROFILE_ID,
    }), [PROFILE_ID]);

    const {
        data: pageData,
        isUninitialized,
        isFetching: isPageFetching,
        isLoading: isPageLoading,
        refetch,
    } = useGetPageResultsQuery({
        page: PAGE_NAME,
        role,
        ...paginationParams,
    });

    const [triggerGetPageContent] = useLazyGetPageContentQuery();

    const [allContents, setAllContents] = useState<any[][]>([]);
    const [categoryPageInfo, setCategoryPageInfo] = useState<Record<number, {page: number; hasMore: boolean}>>({});
    const [slicedPreviewVideos, setSlicedPreviewVideos] = useState<any[]>([]);
    const [isFetchingContent, setIsFetchingContent] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

    const {isPlay} = useSelector((state: RootState) => state.videoPlayer);

    const currentVideo = useMemo(
        () => slicedPreviewVideos?.[currentSlideIndex],
        [slicedPreviewVideos, currentSlideIndex]
    );

    const fetchAllContent = useCallback(
        async (opts?: { isRefresh?: boolean }) => {
            const isRefresh = Boolean(opts?.isRefresh);
            if (!pageData?.results.length) return;
            if (!isRefresh) setIsFetchingContent(true);
            try {
                const results = await Promise.all(
                    pageData.results.map((item) =>
                        triggerGetPageContent({
                            page: PAGE_NAME,
                            role,
                            content_id: item.id,
                            ...paginationParams
                        }).unwrap()
                    )
                );

                const mappedResults = results.map((r) => r?.results ?? []);
                const nextCategoryPageInfo = pageData.results.reduce<Record<number, {page: number; hasMore: boolean}>>(
                    (acc, category, index) => {
                        acc[Number(category.id)] = {
                            page: paginationParams.page_number,
                            hasMore: Boolean(results[index]?.next),
                        };
                        return acc;
                    },
                    {}
                );
                setAllContents(mappedResults);
                setCategoryPageInfo(nextCategoryPageInfo);
                if (!isRefresh) {
                    const firstNonEmptyCategory = mappedResults.find(category => category?.length > 0);
                    const firstCategoryVideos =
                        firstNonEmptyCategory?.slice(0, PREVIEW_VIDEOS_COUNT) ?? [];
                    setSlicedPreviewVideos(firstCategoryVideos);
                    setCurrentSlideIndex(0);
                }
            } catch (error) {
                console.error("Error fetching page content:", error);
            } finally {
                setIsFetchingContent(false);
            }
        },
        [pageData, role, paginationParams, triggerGetPageContent]
    );

    const lastCategoriesKeyRef = useRef<string>("");

    useEffect(() => {
        if (!pageData?.results.length) return;
        const key = pageData.results.map((c) => c.id).join(",");
        if (lastCategoriesKeyRef.current === key) return;

        lastCategoriesKeyRef.current = key;
        fetchAllContent({isRefresh: false});
    }, [pageData, fetchAllContent]);


    const refreshAll = useCallback(async () => {
        try {
            await refetch();
        } finally {
            await fetchAllContent({isRefresh: true});
        }
    }, [refetch, fetchAllContent]);

    const isAnyFetching = isPageLoading || isPageFetching || isFetchingContent;

    if (isAnyFetching) {
        return <Loader className={styles.loader}/>;
    }


    return (
            <Box>
                <PreviewCarouselSlideshow
                    videos={slicedPreviewVideos}
                    activeIndex={currentSlideIndex}
                    onChange={setCurrentSlideIndex}
                    isLocked={isPlay}
                >
                    <div className={styles.content}>
                        {innerWidth > TABLET_VERSION && (
                            <Typography variant="h4" className={styles.title}>
                                {t("Aside.home")}
                            </Typography>
                        )}

                        {currentVideo && (
                            <div
                                style={{
                                    zIndex: 1000001,
                                    position: "relative",
                                    marginBottom: "20px",
                                }}
                            >
                                <FilmDescription
                                    data={currentVideo}
                                    dataPreview={slicedPreviewVideos}
                                    onSlideChange={setCurrentSlideIndex}
                                    mobileBannerLayout
                                />
                            </div>
                        )}

                        {isPlay && (
                            <VideoPlayerPreview
                                data={slicedPreviewVideos}
                                currentSlideIndex={currentSlideIndex}
                            />
                        )}
                    </div>
                </PreviewCarouselSlideshow>

                <FilmLists
                    isWithoutTabs
                    refresh={refreshAll}
                    dataContent={pageData?.results}
                    allContents={allContents}
                    setAllContents={setAllContents}
                    categoryPageInfo={categoryPageInfo}
                    setCategoryPageInfo={setCategoryPageInfo}
                    pageName={PAGE_NAME}
                    canRefresh={!isUninitialized}
                    isLoading={isPageFetching}
                    paginationParams={paginationParams}
                />
            </Box>
    );
};

export default Home;
